import type { Mission, MissionCandidature } from '@/lib/missions';
import { CANDIDATURE_STATUT_META, MISSION_PLATFORMS, MISSION_STATUT_META, missionAbonnesTotal } from '@/lib/missions';

interface Props {
  mission: Mission;
  isMine: boolean;
  myCandidature?: MissionCandidature | null;
  candidaturesCount: number;
  onPostuler: () => void;
  onManage: () => void;
  onDeliver: () => void;
}

function fmtAmount(value: number | null, devise: string): string {
  const n = Number(value) || 0;
  return `${n.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${devise}`;
}

function fmtDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

/** Carte d'une campagne de la marketplace de missions. */
export default function MissionCard({
  mission,
  isMine,
  myCandidature,
  candidaturesCount,
  onPostuler,
  onManage,
  onDeliver,
}: Props) {
  const devise = mission.monaie || 'MAD';
  const statutMeta = MISSION_STATUT_META[mission.statut] || MISSION_STATUT_META.ouverte;
  const candMeta = myCandidature ? CANDIDATURE_STATUT_META[myCandidature.statut] : null;
  const plateformes = (mission.plateformes || []).filter(Boolean);
  const media = (mission.media || []).filter((m) => m && m.url);
  const cover = media.find((m) => m.type !== 'video') || media[0];
  /** Le détail est accessible au propriétaire et à tout candidat déjà inscrit. */
  const openable = isMine || !!myCandidature;

  return (
    <article className="flex flex-col bg-background-50 border border-background-200/70 rounded-lg overflow-hidden h-full">
      {cover ? (
        <div className="relative w-full h-24 md:h-32 bg-background-100 overflow-hidden">
          {cover.type === 'video' ? (
            <video src={cover.url} className="w-full h-full object-cover object-top" muted playsInline />
          ) : (
            <img src={cover.url} alt={mission.titre} className="w-full h-full object-cover object-top" />
          )}
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-foreground-950/70 text-background-50 text-[10px] font-semibold whitespace-nowrap">
            {media.length} média{media.length > 1 ? 's' : ''}
          </span>
        </div>
      ) : (
        <div className="w-full h-24 bg-secondary-50 flex items-center justify-center">
          <i className="ri-megaphone-line text-2xl text-secondary-400"></i>
        </div>
      )}

      <div className="flex flex-col flex-1 p-3 md:p-4">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h3
            onClick={openable ? onManage : undefined}
            role={openable ? 'button' : undefined}
            tabIndex={openable ? 0 : undefined}
            onKeyDown={openable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onManage(); } } : undefined}
            title={openable ? 'Ouvrir le détail de la mission' : undefined}
            className={`text-[13px] md:text-sm font-bold font-heading text-foreground-950 line-clamp-2 leading-snug ${openable ? 'cursor-pointer hover:text-primary-600 transition-colors' : ''}`}
          >
            {mission.titre}
          </h3>
          <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${isMine ? 'bg-primary-100 text-primary-700' : statutMeta.className}`}>
            {isMine ? 'Ma campagne' : statutMeta.label}
          </span>
        </div>

          <p className="text-[11px] text-foreground-500 mb-2 truncate">par {mission.auteur_nom || 'un membre Zifek'}</p>

        {mission.description && (
          <p className="hidden md:block text-xs text-foreground-600 leading-relaxed line-clamp-2 mb-3">{mission.description}</p>
        )}

        <div className="flex flex-wrap items-center gap-1 md:gap-1.5 mb-2 md:mb-3">
          {plateformes.map((key) => {
            const meta = MISSION_PLATFORMS.find((p) => p.key === key);
            return (
              <span key={key} className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium whitespace-nowrap">
                <i className={`${meta?.icon || 'ri-share-line'} mr-1`}></i>
                {meta?.label || key}
              </span>
            );
          })}
          {mission.pays && mission.pays !== 'International' && (
            <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium whitespace-nowrap">
              <i className="ri-global-line mr-1"></i>
              {mission.pays}
            </span>
          )}
          {mission.pays === 'International' && (
            <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-[10px] font-semibold whitespace-nowrap">
              🌍 International
            </span>
          )}
          {mission.ville && (
            <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] font-medium whitespace-nowrap">
              <i className="ri-map-pin-line mr-1"></i>
              {mission.ville}
            </span>
          )}
          {mission.date_limite && (
            <span className="px-2 py-0.5 rounded-full bg-accent-50 text-accent-800 text-[10px] font-medium whitespace-nowrap">
              <i className="ri-calendar-line mr-1"></i>
              jusqu&apos;au {fmtDate(mission.date_limite)}
            </span>
          )}
        </div>

        <div className="mt-auto flex items-end justify-between gap-2 pt-3 border-t border-background-200/60">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Tarif</p>
            <p className="text-xs md:text-sm font-bold text-foreground-950 whitespace-nowrap">
              {fmtAmount(mission.tarif_1000_vues, devise)}
              <span className="hidden md:inline text-[10px] font-medium text-foreground-500"> / 1000 vues</span>
            </p>
          </div>

          {isMine ? (
            <button
              onClick={onManage}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-group-line"></i>
              <span className="hidden md:inline">
                {candidaturesCount} candidat{candidaturesCount > 1 ? 's' : ''}
              </span>
              <span className="md:hidden">{candidaturesCount}</span>
            </button>
          ) : myCandidature?.statut === 'acceptee' || myCandidature?.statut === 'rejetee' || myCandidature?.statut === 'livree' ? (
            <button
              onClick={onDeliver}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors"
            >
              <i className="ri-upload-cloud-2-line"></i>
              <span className="hidden md:inline">Mes liens</span>
            </button>
          ) : myCandidature ? (
            <button
              onClick={onManage}
              className={`px-2.5 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer ${candMeta?.className || 'bg-background-100 text-foreground-600'}`}
              title="Suivre ma candidature"
            >
              {candMeta?.label || myCandidature.statut}
            </button>
          ) : mission.statut === 'ouverte' ? (
            <button
              onClick={onPostuler}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-send-plane-line"></i>
              Postuler
            </button>
          ) : (
            <span className="px-2.5 py-1.5 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium whitespace-nowrap">
              Candidatures fermées
            </span>
          )}
        </div>

        {isMine && myCandidature == null && (
          <p className="mt-2 text-[10px] text-foreground-400 flex items-center gap-1">
            <i className="ri-information-line"></i>
            {missionAbonnesTotal(null) ? '' : `${candidaturesCount} candidature(s) reçue(s)`}
          </p>
        )}
        {isMine && (
          <p className="hidden md:inline-flex mt-2 text-[11px] text-foreground-400 items-center gap-1">
            <i className="ri-chat-3-line"></i>
            Ouvrez la campagne pour écrire aux candidats
          </p>
        )}
      </div>
    </article>
  );
}